// Live drive monitor stub. Given a sessionId it opens the live WebSocket and
// streams the newline-delimited JSON events the backend echoes to subscribers.
//
// See api.ts openLive for the important caveat: browsers cannot set the x-wd-*
// HMAC headers on a WebSocket upgrade, so the current handshake will reject this
// connection with 401. This view is wired end-to-end so it works the moment the
// backend accepts a query-param / subprotocol token.

import { useEffect, useRef, useState } from "react";
import { isPlaceholder, openLive } from "../api.js";

type Status = "idle" | "connecting" | "open" | "closed" | "error";

export function LiveMonitor() {
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [lines, setLines] = useState<string[]>([]);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    return () => socketRef.current?.close();
  }, []);

  function connect() {
    socketRef.current?.close();
    setLines([]);
    if (isPlaceholder) {
      setStatus("error");
      setLines(["placeholder mode: set VITE_TELEMETRY_URL to connect to a live session"]);
      return;
    }
    const ws = openLive(sessionId.trim() || "live");
    if (!ws) {
      setStatus("error");
      return;
    }
    socketRef.current = ws;
    setStatus("connecting");
    ws.addEventListener("open", () => setStatus("open"));
    ws.addEventListener("message", (e: MessageEvent) => {
      const data = typeof e.data === "string" ? e.data : "[binary frame]";
      setLines((prev) => [...prev.slice(-200), data]);
    });
    ws.addEventListener("close", () => setStatus("closed"));
    ws.addEventListener("error", () => setStatus("error"));
  }

  function disconnect() {
    socketRef.current?.close();
    socketRef.current = null;
    setStatus("closed");
  }

  return (
    <div className="panel">
      <h3>Live drive monitor</h3>
      <p className="muted">
        Connects to <code>/nav-telemetry/live?tripId=&lt;sessionId&gt;</code>. Handshake auth caveat: see
        api.ts (browser WebSocket cannot send HMAC headers).
      </p>
      <div className="scrubber">
        <input
          type="text"
          placeholder="sessionId (tripId)"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          style={{
            flex: 1,
            background: "var(--panel-2)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "6px 10px",
          }}
        />
        <button onClick={connect}>Connect</button>
        <button onClick={disconnect}>Disconnect</button>
        <span className="muted">status: {status}</span>
      </div>
      <pre className="detail" style={{ maxHeight: 300 }}>
        {lines.length ? lines.join("\n") : "no events yet"}
      </pre>
    </div>
  );
}
